import { useCallback, useRef, useState } from 'react'

import { FirebaseApp } from 'firebase/app'
import { Messaging } from 'firebase/messaging'

import { useDomain } from '@tribeplatform/react-components/common/hooks'

import {
  getFcmToken,
  getFirebaseApp,
  getFirebaseMessaging,
} from './firebase-messaging.js'

export interface FcmTokenState {
  /** Current device FCM token, or null when unavailable / not yet fetched. */
  token: string | null
  /** Lazily initializes Firebase (idempotent). */
  initialize: () => void
  /**
   * Fetches and stores the device token. Caller must ensure permission is
   * `granted` first — `getToken()` prompts otherwise.
   */
  fetchToken: () => Promise<string | null>
  /** Drops the locally-held token (e.g. after the permission is revoked). */
  clearToken: () => void
}

/**
 * Owns the Firebase Cloud Messaging device token: lazy Firebase init and
 * fetching/clearing the token. It deliberately knows nothing about permissions
 * or subscriber settings — the caller decides when it's safe to fetch.
 */
export const useFcmToken = (): FcmTokenState => {
  const { getResourceUrl } = useDomain()
  const firebaseApp = useRef<FirebaseApp | null>(null)
  const firebaseMessaging = useRef<Messaging | null>(null)
  const [token, setToken] = useState<string | null>(null)

  const initialize = useCallback(() => {
    if (firebaseApp.current) {
      return
    }
    firebaseApp.current = getFirebaseApp()
    firebaseMessaging.current = getFirebaseMessaging(firebaseApp.current)
  }, [])

  const fetchToken = useCallback(async (): Promise<string | null> => {
    initialize()
    const next = await getFcmToken(firebaseMessaging.current, getResourceUrl)
    setToken(next)
    return next
  }, [getResourceUrl, initialize])

  const clearToken = useCallback(() => setToken(null), [])

  return { token, initialize, fetchToken, clearToken }
}
